import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { useAppDispatch } from "../../../store";
import {
    hideTransparentPreloaderAction,
    showNotificationAction,
    showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
    getCandidateData,
    saveCandidateLoadAllowanceThunk,
} from "../../../store/superadmin/thunks";
import { ICandidateDetails } from "../../../store/superadmin/types";
import { capitalizeName } from "../../../utils/stringUtils";
import CustomButton from "../../UI/Buttons/CustomButton";
import PlaceHolderImage from "../../UI/Icons/PlaceHolderImage";
import RoundedTick from "../../UI/Icons/RoundedTick";
import RadioInput from "../../UI/Inputs/RadioInput";
import PopupModal from "../../UI/Popup";
import StarRating from "../../UI/StarRating";

interface LoadAllowanceProps {
  showAllowanceLoad: boolean;
  onClickClose: () => void;
  reviewCandidateData: ICandidateDetails | null;
  convertToTitleCase: (str: string) => string;
  setShowAllowanceLoad: (show: boolean) => void;
  setShowModal: (show: boolean) => void;
}

/**
 * LoadAllowance component displays a modal popup that allows a super admin to assign a loading allowance
 * (additional hourly rate) to a candidate. It shows candidate details, their photo, qualifications, and rating,
 * and provides radio button options for selecting the loading amount. Upon saving, it dispatches actions to
 * update the candidate's loading allowance and shows notifications based on the result.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} props.showAllowanceLoad - Controls the visibility of the modal popup.
 * @param {() => void} props.onClickClose - Callback to close the modal.
 * @param {any} props.reviewCandidateData - Candidate data object containing profile, photo, name, etc.
 * @param {(str: string) => string} props.convertToTitleCase - Utility function to convert strings to title case.
 * @param {(show: boolean) => void} props.setShowAllowanceLoad - Function to set the visibility of the modal.
 *
 * @returns {JSX.Element} The rendered LoadAllowance modal component.
 */
const LoadAllowance: React.FC<LoadAllowanceProps> = ({
  showAllowanceLoad,
  onClickClose,
  reviewCandidateData,
  convertToTitleCase,
  setShowAllowanceLoad,
  setShowModal,
}) => {
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      loading_amount: reviewCandidateData?.profile?.loading_amount || 0,
      candidate_id: reviewCandidateData?.id || null,
    },
    onSubmit: async (values) => {
      dispatch(showTransparentPreloaderAction());
      try {
        const response = await dispatch(
          saveCandidateLoadAllowanceThunk(values)
        );
        if (isFulfilled(response)) {
          setShowAllowanceLoad(false); // closig this popup and showing notification
          dispatch(showNotificationAction("Saved."));

          setTimeout(async () => {
            loadCandidateData();
          }, NOTIFICATION_TIME_OUT);
        }
      } catch (error) {
        dispatch(
          showNotificationAction("An error occurred. Please try again.")
        );
      } finally {
        dispatch(hideTransparentPreloaderAction());
      }
    },
  });

  const { values, handleChange, handleSubmit, setFieldValue } = formik;

  const handleFieldChange = (field: string, value: any) => {
    setFieldValue(field, value);
  };

  // Loading candidate data when the component mounts or when reviewCandidateData changes
  useEffect(() => {
    setFieldValue("candidate_id", reviewCandidateData?.id || null);
    setFieldValue(
      "loading_amount",
      reviewCandidateData?.profile?.loading_amount || 0
    );
  }, [reviewCandidateData, setFieldValue, showAllowanceLoad]);

  // After saving the loading allowance, we want to reload the candidate data
  const loadCandidateData = async () => {
    dispatch(showTransparentPreloaderAction());
    // setShowAllowanceLoad(true); // reopen the popup to show the updated data
    await dispatch(
      getCandidateData({ candidate_id: reviewCandidateData?.uuid || "" })
    );
    setShowModal(true);
    dispatch(hideTransparentPreloaderAction());
  };

  // Handle closing the modal
  const handleOnClickClose = () => {
    setFieldValue("loading_amount", 0);
    onClickClose();
  };



  return (
    <PopupModal
      visible={showAllowanceLoad}
      showCloseBtn={true}
      onClickClose={handleOnClickClose}
      customWidth={650}
    >
      <div className="d-flex flex-column gap-15 text-break">
        <div className="fs-22 fw-400 text-black">Add Loading to Employee</div>
        <div className="w-100 h-1px bg-off-white"></div>

        <div className="d-flex gap-20 align-items-center">
          {reviewCandidateData?.photo === null ||
          reviewCandidateData?.photo?.length === 0 ? (
            <PlaceHolderImage />
          ) : (
            <img
              src={reviewCandidateData?.photo ?? undefined}
              alt="Candidate"
              className="w-50px h-50px rounded-circle"
            />
          )}
          <div className="d-flex flex-column gap-3 fs-16 text-black">
            <div className="fw-700">
              {capitalizeName(reviewCandidateData?.first_name ?? "")} {capitalizeName(reviewCandidateData?.last_name ?? "")}{" "}
            </div>
            <div>
              {convertToTitleCase(
                reviewCandidateData?.qualifications?.qualification ?? ""
              )}
            </div>
            <StarRating
              rating={
                reviewCandidateData?.rating?.average_rating
                  ? reviewCandidateData?.rating?.average_rating
                  : 0
              }
            />
          </div>
        </div>
        <div className="h-1px"></div>
        <div className="fs-16 fw-700 text-black">
          Indicate the loading amount to add to the employee's completed jobs:
        </div>
        <div className="d-flex flex-column gap-10">
          <RadioInput
            label="$2.00 per hour"
            name="loading_amount"
            value={2}
            checked={values.loading_amount === 2}
            onChange={() => handleFieldChange("loading_amount", 2)}
          />
          <RadioInput
            label="$4.00 per hour"
            name="loading_amount"
            value={4}
            checked={values.loading_amount === 4}
            onChange={() => handleFieldChange("loading_amount", 4)}
          />
          <RadioInput
            label="$6.00 per hour"
            name="loading_amount"
            value={6}
            checked={values.loading_amount === 6}
            onChange={() => handleFieldChange("loading_amount", 6)}
          />
          <RadioInput
            label="$8.00 per hour"
            name="loading_amount"
            value={8}
            checked={values.loading_amount === 8}
            onChange={() => handleFieldChange("loading_amount", 8)}
          />
        </div>
        <div className="h-1px"></div>
        <div className="d-flex gap-20 justify-content-end">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black mobile-w-100"
            // isLoading={InProgress}
            onClick={handleSubmit}
            disabled={values.loading_amount === 0}
          >
            <RoundedTick
              color={values.loading_amount === 0 ? "#aaaaaa" : "#000000"}
            />
            {"Save"}
          </CustomButton>
        </div>
      </div>
    </PopupModal>
  );
};

export default LoadAllowance;
